/* eslint-disable sonarjs/cognitive-complexity */
import {
  BaSeBreakpointContext,
  BaSeGrid,
  BaSeGridItem,
  BaSeRadio,
} from '@base/react';
import * as Sebrae from '@sebrae/react-core';
import React from 'react';

import { useAnswerController } from '../../../../hooks/controllers/answer-controller';
import { useInterviewContext } from '../../context/interview-context';
import CommonButton from '../common-button/common-button';
import CommonCheckbox from '../common-checkbox/common-checkbox';
import CommonInput from '../common-input/common-input';
import CommonSelect from '../common-select/common-select';
import CommonTextarea from '../common-textarea/common-textarea';
import {
  Container,
  Content,
  ErrorMessage,
} from './questionnaire-interview-startup-style';

const cache = Sebrae.StorageUtil.inTemporaryStore();
const cacheKey = '@empretec:dataQuestionaireStartup';

const initialState = {
  stageStartup: { question: { id: null }, questionOption: { id: '' } },
  startupName: { question: { id: 12 }, answer: '' },
  fundationYear: { question: { id: 13 }, answer: 0 },
  cnpj: { question: { id: 14 }, answer: '' },
  site: { question: { id: 15 }, answer: '' },
  insta: { question: { id: 16 }, answer: '' },
  qtdColaboradores: { question: { id: 17 }, answer: 0 },
  startupVinculo: { question: { id: null }, questionOption: { id: '' } },
  textResolvProblem: { question: { id: 19 }, answer: '' },
  optionDisponibility: { question: { id: null }, questionOption: { id: '' } },
};

const optionsEstagioStartup = [
  {
    id: 1,
    value: 'Estou desenvolvendo uma ideia de startup.',
  },
  {
    id: 2,
    value: 'Estou validando uma ideia de startup.',
  },
  {
    id: 3,
    value: 'Já tenho uma startup com receita de clientes.',
  },
];

const optionsDiferencaStartup = [
  {
    id: 7,
    value: 'Já estudei sobre startup e interajo com outros empreendedores.',
  },
  {
    id: 8,
    value: 'Faço parte de um grupo de empreendedores da minha região.',
  },
  {
    id: 9,
    value: 'Sempre frequento os meetups da minha região.',
  },
  {
    id: 10,
    value: 'Já fui participante de um startup weekend.',
  },
  {
    id: 11,
    value: 'Já fui facilitador de um startup weekend.',
  },
  {
    id: 12,
    value: 'Participo de eventos de startup em outras regiões.',
  },
];

const disponibilityOptions = [
  {
    id: 14,
    value: 'Sim',
  },
  {
    id: 13,
    value: 'Não',
  },
];

const REQUIRED = 'Campo obrigatório não informado.';

const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const startYear = 1988;
  const years = [];

  for (let year = currentYear; year >= startYear; year--) {
    years.push({ id: year, value: year.toString() });
  }

  return years;
};

const yearOptions = generateYearOptions();

const QuestionnaireInterviewStartup = ({
  setFinishedInterview,
  setStatusInterview,
}) => {
  const [form, setForm] = React.useState(initialState);
  const [conteudosVinculadosObj, setConteudosVinculadosObj] = React.useState(
    {},
  );
  const breakpointController = React.useContext(BaSeBreakpointContext);
  const { sendAnswerInterview } = useAnswerController();
  const { interview } = useInterviewContext();

  const size =
    breakpointController.inDesktop ||
    breakpointController.inDesktopLarge ||
    breakpointController.inTablet
      ? 6
      : 12;

  const [formValidation, setFormValidation] = React.useState(false);
  const disabledCheckbox = false;

  const onChangeConteudoVinculado = (optionId, currentValue) => {
    setConteudosVinculadosObj({
      ...conteudosVinculadosObj,
      [optionId]: !currentValue,
    });
  };

  const validDiferenciais = React.useCallback(() => {
    let arr = [];
    for (const chave in conteudosVinculadosObj) {
      if (conteudosVinculadosObj.hasOwnProperty(chave)) {
        arr.push(conteudosVinculadosObj[chave]);
      }
    }
    const value = arr.filter((e) => e).length;
    return value > 0;
  }, [conteudosVinculadosObj]);

  const validationMandatoryFields = React.useCallback(() => {
    setFormValidation(true);

    if (
      form.stageStartup.question.id === null ||
      form.startupVinculo.question.id === null ||
      !validDiferenciais() ||
      form.optionDisponibility.question.id === null ||
      form.qtdColaboradores.answer === '' ||
      form.qtdColaboradores.answer === 0
    ) {
      return false;
    }
    return true;
  }, [form, validDiferenciais]);

  const formatedData = React.useCallback(() => {
    let arr = [];
    for (const chave in conteudosVinculadosObj) {
      if (conteudosVinculadosObj.hasOwnProperty(chave)) {
        if (conteudosVinculadosObj[chave]) {
          arr.push({
            question: { id: 20 },
            questionOption: { id: Number(chave) },
          });
        }
      }
    }

    let answersData = [];
    for (const key in form) {
      if (form.hasOwnProperty(key)) {
        answersData.push(form[key]);
      }
    }

    const data = {
      answers: [...arr, ...answersData],
      idInterview: interview.id,
    };

    return data;
  }, [form, interview, conteudosVinculadosObj]);

  const send = React.useCallback(() => {
    if (!validationMandatoryFields()) {
      return;
    }

    const data = formatedData();

    cache.setItem(cacheKey, data);

    sendAnswerInterview(data, interview?.module?.id)
      .then(() => {
        setFinishedInterview(true);
        setStatusInterview('PROCESSING');
        cache.removeItem(cacheKey);
      })
      .catch((error) => {
        console.error('Erro ao enviar', error);
        setFinishedInterview(true);
        setStatusInterview('ERROR');
      });
  }, [
    validationMandatoryFields,
    setFinishedInterview,
    setStatusInterview,
    sendAnswerInterview,
    interview,
    formatedData,
  ]);

  return (
    <Container>
      <BaSeGrid gap={6}>
        <BaSeGridItem size={12}>
          <strong>Questionário de seleção</strong>
        </BaSeGridItem>
        <BaSeGridItem size={12}>
          <p>
            Esta etapa consiste em um questionário em que iremos conhecer um
            pouco mais sobre o perfil da sua startup para assim avaliar sua
            participação no seminário.
          </p>
        </BaSeGridItem>
      </BaSeGrid>

      <Content>
        <BaSeGrid gap={6}>
          <BaSeGridItem size={12}>
            <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              Estágio da startup:
            </p>
            {optionsEstagioStartup.map((option) => (
              <BaSeRadio
                key={option.id}
                checked={form.stageStartup.questionOption.id === option.id}
                onChange={() => {
                  setForm({
                    ...form,
                    stageStartup: {
                      question: { id: 11 },
                      questionOption: { id: option.id },
                    },
                  });
                }}
                color="default"
                id={option.id}
                label={option.value}
                name="stageStartup"
                size="small"
              />
            ))}
            {formValidation && form.stageStartup.question.id === null && (
              <ErrorMessage>{REQUIRED}</ErrorMessage>
            )}
          </BaSeGridItem>

          <BaSeGridItem size={12}>
            <CommonInput
              label="Nome da startup:"
              placeholder="Nome da startup"
              name="startupName"
              value={form.startupName.answer}
              onChange={(e) => {
                setForm({
                  ...form,
                  startupName: {
                    question: { id: 12 },
                    answer: e.target.value,
                  },
                });
              }}
            />
          </BaSeGridItem>

          <BaSeGridItem size={size}>
            <CommonSelect
              label="Ano fundação"
              placeholder="Selecione o ano"
              value={form.fundationYear.answer}
              onChange={(e) => {
                setForm({
                  ...form,
                  fundationYear: {
                    question: { id: 13 },
                    answer: e.target.value,
                  },
                });
              }}
              options={yearOptions}
              name="fundationYear"
            />
          </BaSeGridItem>

          <BaSeGridItem size={size}>
            <CommonInput
              label="CNPJ:"
              mask="cnpj"
              placeholder="CNPJ"
              name="cnpj"
              value={form.cnpj.answer}
              onChange={(e) => {
                setForm({
                  ...form,
                  cnpj: {
                    question: { id: 14 },
                    answer: e.target.value,
                  },
                });
              }}
            />
          </BaSeGridItem>

          <BaSeGridItem size={12}>
            <CommonInput
              label="Site:"
              placeholder="www.example.com"
              name="site"
              value={form.site.answer}
              onChange={(e) => {
                setForm({
                  ...form,
                  site: {
                    question: { id: 15 },
                    answer: e.target.value,
                  },
                });
              }}
            />
          </BaSeGridItem>

          <BaSeGridItem size={12}>
            <CommonInput
              label="Instagram:"
              placeholder="@exempleName"
              name="insta"
              value={form.insta.answer}
              onChange={(e) => {
                setForm({
                  ...form,
                  insta: {
                    question: { id: 16 },
                    answer: e.target.value,
                  },
                });
              }}
            />
          </BaSeGridItem>

          <BaSeGridItem size={size}>
            <CommonInput
              label="Nº Colaboradores:"
              required
              placeholder="0"
              name="qtdColaboradores"
              value={form.qtdColaboradores.answer.toString()}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setForm({
                    ...form,
                    qtdColaboradores: {
                      question: { id: 17 },
                      answer: '',
                    },
                  });
                  return;
                }
                const newValue = Number(value);
                if (isNaN(newValue)) return;

                setForm({
                  ...form,
                  qtdColaboradores: {
                    question: { id: 17 },
                    answer: newValue,
                  },
                });
              }}
              error={
                formValidation &&
                (form.qtdColaboradores.answer === '' ||
                  !form.qtdColaboradores.answer)
                  ? REQUIRED
                  : ''
              }
            />
          </BaSeGridItem>

          <BaSeGridItem size={size}>
            <CommonSelect
              label="Vínculo com a startup:"
              placeholder="Selecione seu vínculo"
              value={form.startupVinculo.questionOption.id}
              onChange={(e) => {
                setForm({
                  ...form,
                  startupVinculo: {
                    question: { id: 18 },
                    questionOption: { id: e.target.value },
                  },
                });
              }}
              options={[
                { id: 4, value: 'Sócio fundador' },
                { id: 5, value: 'Colaborador' },
                { id: 6, value: 'Outro' },
              ]}
              required
              name="startupVinculo"
              error={
                formValidation && !form.startupVinculo.questionOption.id
                  ? REQUIRED
                  : ''
              }
            />
          </BaSeGridItem>

          <BaSeGridItem size={12}>
            <CommonTextarea
              label="Qual problema sua startup resolve e qual a sua proposta de solução?"
              placeholder="Descreva aqui..."
              name="textResolvProblem"
              value={form.textResolvProblem.answer}
              maxWords={200}
              onChange={(e) => {
                setForm({
                  ...form,
                  textResolvProblem: {
                    question: { id: 19 },
                    answer: e.target.value,
                  },
                });
              }}
            />
          </BaSeGridItem>

          <BaSeGridItem size={12}>
            <p style={{ fontWeight: 'bold', marginBottom: '12px' }}>
              Sinalize seus diferenciais no ambiente startup:
            </p>
            {optionsDiferencaStartup.map((option) => (
              <CommonCheckbox
                key={option.id}
                id={option.id}
                label={option.value}
                name="diferencaStartup"
                checked={conteudosVinculadosObj[option.id] || false}
                disabled={
                  !conteudosVinculadosObj[option.id] && disabledCheckbox
                }
                onChange={(isChecked) => {
                  onChangeConteudoVinculado(
                    option.id,
                    conteudosVinculadosObj[option.id],
                  );
                }}
              />
            ))}
            {formValidation && !validDiferenciais() && (
              <ErrorMessage>{REQUIRED}</ErrorMessage>
            )}
          </BaSeGridItem>

          <BaSeGridItem size={12}>
            <p style={{ fontWeight: 'bold', marginBottom: '12px' }}>
              O Empretec Startup consumirá tempo integral durante os 5 dias do
              seminário, tanto no período em sala quanto fora dela. Você tem a
              disponibilidade de tempo necessária para participar integralmente?
            </p>

            {disponibilityOptions.map((option) => (
              <BaSeRadio
                key={option.id}
                checked={
                  form.optionDisponibility.questionOption.id === option.id
                }
                onChange={() => {
                  setForm({
                    ...form,
                    optionDisponibility: {
                      question: { id: 21 },
                      questionOption: { id: option.id },
                    },
                  });
                }}
                color="default"
                id={option.id}
                label={option.value}
                name="optionDisponibility"
                size="small"
              />
            ))}
            {formValidation &&
              form.optionDisponibility.question.id === null && (
                <ErrorMessage>{REQUIRED}</ErrorMessage>
              )}
          </BaSeGridItem>
        </BaSeGrid>
      </Content>

      <div style={{ margin: '0.5em 0' }}>
        <CommonButton
          variant="primary"
          isBold={true}
          backgroundColor="#0000C7"
          onClick={send}
        >
          ENVIAR QUESTIONÁRIO
        </CommonButton>
      </div>
    </Container>
  );
};

export default QuestionnaireInterviewStartup;
